// LanguageContext.jsx
import React, { createContext, useState, useContext, useEffect } from "react";
import {
  getCurrentLanguage,
  setLanguage as setAppLanguage,
  initializeLanguage,
  AVAILABLE_LANGUAGES,
  t,
} from "@/utils/language";

// Create the context
const LanguageContext = createContext();

// Custom hook to use the language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

// Language provider component
export const LanguageProvider = ({ children }) => {
  const [currentLang, setCurrentLang] = useState("");
  const [isRTL, setIsRTL] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize on mount
  useEffect(() => {
    const initialLang = initializeLanguage();
    setCurrentLang(initialLang);
    setIsRTL(AVAILABLE_LANGUAGES[initialLang]?.dir === "rtl");

    // Update document direction and language attributes
    document.documentElement.dir =
      AVAILABLE_LANGUAGES[initialLang]?.dir || "ltr";
    document.documentElement.lang = initialLang;

    // Set loading to false after initialization
    setIsLoading(false);

    // Add listener for language changes from other components
    const handleLanguageChange = () => {
      const lang = getCurrentLanguage();
      setCurrentLang(lang);
      setIsRTL(AVAILABLE_LANGUAGES[lang]?.dir === "rtl");

      // Update document direction and language attributes when language changes
      document.documentElement.dir = AVAILABLE_LANGUAGES[lang]?.dir || "ltr";
      document.documentElement.lang = lang;
    };

    window.addEventListener("languagechange", handleLanguageChange);

    return () => {
      window.removeEventListener("languagechange", handleLanguageChange);
    };
  }, []);

  // Change language function
  const changeLanguage = (langCode) => {
    if (setAppLanguage(langCode)) {
      setCurrentLang(langCode);
      setIsRTL(AVAILABLE_LANGUAGES[langCode]?.dir === "rtl");

      // Update document direction and language attributes
      document.documentElement.dir =
        AVAILABLE_LANGUAGES[langCode]?.dir || "ltr";
      document.documentElement.lang = langCode;

      // Force re-render of components that depend on language
      window.dispatchEvent(new Event("languagechange"));

      // Add a class to the body to trigger CSS transitions if needed
      document.body.classList.remove("lang-en", "lang-ar");
      document.body.classList.add(`lang-${langCode}`);

      return true;
    }
    return false;
  };

  // Function to get translated text (wrapper around the t function)
  const translate = (key, defaultValue) => {
    return t(key, defaultValue);
  };

  // Format date according to current language
  const formatDate = (date, options = {}) => {
    try {
      return new Date(date).toLocaleDateString(
        currentLang === "ar" ? "ar-AE" : "en-US",
        options
      );
    } catch (error) {
      console.error("Error formatting date:", error);
      return date;
    }
  };

  // Format number according to current language
  const formatNumber = (number, options = {}) => {
    try {
      return new Intl.NumberFormat(
        currentLang === "ar" ? "ar-AE" : "en-US",
        options
      ).format(number);
    } catch (error) {
      console.error("Error formatting number:", error);
      return number;
    }
  };

  // Format currency according to current language
  const formatCurrency = (amount, currency = "AED") => {
    try {
      return new Intl.NumberFormat(currentLang === "ar" ? "ar-AE" : "en-US", {
        style: "currency",
        currency: currency,
      }).format(amount);
    } catch (error) {
      console.error("Error formatting currency:", error);
      return `${amount} ${currency}`;
    }
  };

  // Context value with all language-related functions and data
  const contextValue = {
    currentLang,
    isRTL,
    isLoading,
    changeLanguage,
    languages: AVAILABLE_LANGUAGES,
    t: translate,
    formatDate,
    formatNumber,
    formatCurrency,
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
